/**
 * Приглашение коллеги — право ОТВЕТСТВЕННОГО от контрагента: приглашение создаёт заявку,
 * то есть это способ привести в портал нового человека. Остальным экран не показываем, но
 * решает не он: ВÖİN берётся из карточки приглашающего на бэке, и специалисту там откажут.
 *
 * Отдельный маршрут, а не диалог: форма с четырьмя полями и своим состоянием успеха, и по
 * ней может прийти ссылка от коллеги.
 */
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'
import { errorText } from '../../src/features/edo/errorText'
import { useApp } from '../../src/i18n'
import { tone } from '../../src/theme/tones'
import { Button, FormInput, QueryState, Seg } from '../../src/ui'
import {
  CabinetBack,
  CabinetShell,
} from '../../src/features/cabinet/CabinetShell'

export default function CabinetInviteScreen() {
  const { t, l } = useApp()
  const router = useRouter()

  const profile = useQuery({ queryKey: ['cabinet', 'me'], queryFn: api.cabinetMe })

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<'SIGNER' | 'SPECIALIST'>('SPECIALIST')
  const [localError, setLocalError] = useState('')

  const invite = useMutation({
    mutationFn: () =>
      api.cabinetInvite({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        role,
      }),
  })

  const submit = () => {
    setLocalError('')
    if (!fullName.trim()) return setLocalError(l('cab.signup.fullname'))
    if (!email.includes('@')) return setLocalError(l('cab.signup.email'))
    invite.mutate()
  }

  const error =
    localError || (invite.error instanceof Error ? t(errorText(invite.error)) : '')

  return (
    <CabinetShell>
      <CabinetBack to="contracts" label={l('cab.nav.contracts')} />
      <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4}>
        {l('cab.invite.title')}
      </Text>
      <QueryState q={profile}>
        {profile.data && profile.data.role !== 'RESPONSIBLE' ? (
          // Приглашает только ответственный — говорим это прямо, а не прячем кнопку молча.
          <Text fontSize={13} color="$textMuted">
            {l('cab.invite.onlyresponsible')}
          </Text>
        ) : invite.isSuccess ? (
          <YStack gap={12} maxWidth={460}>
            <Text fontSize={13} color="$textMuted">
              {l('cab.invite.done')}
            </Text>
            <Button block onPress={() => router.replace('/cabinet')}>
              {l('cab.contracts.title')}
            </Button>
          </YStack>
        ) : (
          <YStack gap={12} maxWidth={460}>
            <Text fontSize={13} color="$textMuted">
              {l('cab.invite.hint')}
            </Text>

            <YStack gap={4}>
              <Text fontSize={12} color="$textMuted">{l('cab.signup.fullname')}</Text>
              <FormInput value={fullName} onChangeText={setFullName} autoComplete="name" />
            </YStack>
            <YStack gap={4}>
              <Text fontSize={12} color="$textMuted">{l('cab.signup.email')}</Text>
              <FormInput
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                keyboardType="email-address"
              />
            </YStack>
            <YStack gap={4}>
              <Text fontSize={12} color="$textMuted">{l('cab.signup.phone')}</Text>
              <FormInput value={phone} onChangeText={setPhone} keyboardType="phone-pad" />
            </YStack>

            <YStack gap={6}>
              {/* Своя строка, а не из анкеты: там человек отвечает про СЕБЯ, здесь — про коллегу. */}
              <Text fontSize={12} color="$textMuted">{l('cab.invite.role')}</Text>
              <XStack gap={8} flexWrap="wrap">
                <Seg
                  active={role === 'SPECIALIST'}
                  label={l('cab.signup.role.specialist')}
                  onPress={() => setRole('SPECIALIST')}
                />
                <Seg
                  active={role === 'SIGNER'}
                  label={l('cab.signup.role.signer')}
                  onPress={() => setRole('SIGNER')}
                />
              </XStack>
            </YStack>

            {!!error && (
              <Text fontSize={13} color={tone('red').text}>
                {error}
              </Text>
            )}

            <Button variant="primary" block disabled={invite.isPending} onPress={submit}>
              {invite.isPending ? l('cab.signup.sending') : l('cab.invite.send')}
            </Button>
          </YStack>
        )}
      </QueryState>
    </CabinetShell>
  )
}
